/**
 * parts/prizes.js — the things a player actually takes home.
 *
 * Plushies, goldfish, tickets and tokens. These end up on a shelf in the
 * personal space and in the payout panel, so they are modelled to read at
 * small scale and from any angle.
 *
 * @module assets3d/parts/prizes
 */

import * as THREE from 'three';
import { ART, shade, tint } from '../palette.js';
import { plushFelt, fishScales, ticketFace, paint, showcardText } from '../textures.js';
import { geo, mat, mesh, teardrop } from './core.js';

/** A heart, for the big goose to hug. */
function heartGeo(size = 0.14) {
  return geo(`heart-${size}`, () => {
    const s = new THREE.Shape();
    const k = size;
    s.moveTo(0, -k * 0.75);
    s.bezierCurveTo(k * 0.9, -k * 0.1, k * 0.75, k * 0.75, 0, k * 0.42);
    s.bezierCurveTo(-k * 0.75, k * 0.75, -k * 0.9, -k * 0.1, 0, -k * 0.75);
    const g = new THREE.ExtrudeGeometry(s, {
      depth: k * 0.5, bevelEnabled: true,
      bevelThickness: k * 0.16, bevelSize: k * 0.16, bevelSegments: 2,
      curveSegments: 6,
    });
    g.center();
    return g;
  });
}

/** Two crossed stitches, the classic plush eye. */
function stitchEye(color = ART.ink) {
  const g = new THREE.Group();
  for (const rot of [0.7, -0.7]) {
    const bar = mesh(
      geo('stitch-bar', () => new THREE.BoxGeometry(0.019, 0.004, 0.004)),
      mat(color, { rough: 0.7 }),
      { cast: false, receive: false },
    );
    bar.rotation.z = rot;
    g.add(bar);
  }
  return g;
}

/** A small sitting teddy — the consolation prize. */
function createTeddy() {
  const g = new THREE.Group();
  g.name = 'plush-small';
  const fur = mat(0xffffff, { rough: 0.97, map: plushFelt(0x9b6fd6) });
  const inner = mat(0xffffff, { rough: 0.97, map: plushFelt(0xe4c7a0) });

  const body = mesh(
    geo('teddy-body', () => new THREE.SphereGeometry(0.088, 12, 10)),
    fur,
  );
  body.scale.set(1, 1.06, 0.92);
  body.position.y = 0.098;
  g.add(body);

  const belly = mesh(
    geo('teddy-belly', () => new THREE.SphereGeometry(0.058, 10, 8)),
    inner,
  );
  belly.scale.set(1, 1.02, 0.55);
  belly.position.set(0, 0.088, -0.058);
  g.add(belly);

  const head = mesh(
    geo('teddy-head', () => new THREE.SphereGeometry(0.066, 12, 10)),
    fur,
  );
  head.position.y = 0.238;
  g.add(head);

  const muzzle = mesh(
    geo('teddy-muzzle', () => new THREE.SphereGeometry(0.030, 9, 7)),
    inner,
  );
  muzzle.scale.set(1.1, 0.82, 0.9);
  muzzle.position.set(0, 0.222, -0.052);
  g.add(muzzle);

  const nose = mesh(
    geo('teddy-nose', () => new THREE.SphereGeometry(0.011, 7, 6)),
    mat(ART.ink, { rough: 0.5 }),
    { cast: false },
  );
  nose.scale.set(1.3, 0.9, 0.9);
  nose.position.set(0, 0.230, -0.077);
  g.add(nose);

  for (const s of [-1, 1]) {
    const ear = mesh(
      geo('teddy-ear', () => new THREE.SphereGeometry(0.026, 8, 6)),
      fur,
    );
    ear.scale.set(1, 1, 0.6);
    ear.position.set(0.048 * s, 0.288, 0.004);
    g.add(ear);

    const eye = stitchEye();
    eye.position.set(0.026 * s, 0.252, -0.058);
    g.add(eye);

    // Arms and legs, angled so the bear reads as sitting.
    const arm = mesh(
      geo('teddy-arm', () => new THREE.CapsuleGeometry(0.021, 0.048, 3, 7)),
      fur,
    );
    arm.position.set(0.086 * s, 0.122, -0.020);
    arm.rotation.z = 0.85 * s;
    g.add(arm);

    const leg = mesh(
      geo('teddy-leg', () => new THREE.CapsuleGeometry(0.026, 0.040, 3, 7)),
      fur,
    );
    leg.position.set(0.050 * s, 0.030, -0.052);
    leg.rotation.x = -1.25;
    g.add(leg);

    const pad = mesh(
      geo('teddy-pad', () => new THREE.SphereGeometry(0.019, 8, 6)),
      inner,
      { receive: false },
    );
    pad.scale.set(1, 0.7, 1);
    pad.position.set(0.050 * s, 0.030, -0.094);
    g.add(pad);
  }
  return g;
}

/** The big goose plush, hugging a heart. The top prize on the wall. */
function createGoosePlush() {
  const g = new THREE.Group();
  g.name = 'plush-large';
  const fur = mat(0xffffff, { rough: 0.97, map: plushFelt(0xfbf3e4) });
  const billMat = mat(0xf0a03a, { rough: 0.6 });

  const body = mesh(teardrop(0.155, { seg: 14, taper: 0.46, lift: 0.30, chest: 1.05 }), fur);
  body.scale.set(1, 0.94, 1.18);
  body.position.y = 0.165;
  g.add(body);

  const neck = mesh(
    geo('gplush-neck', () => new THREE.CapsuleGeometry(0.052, 0.10, 4, 9)),
    fur,
  );
  neck.position.set(0, 0.335, -0.058);
  neck.rotation.x = -0.24;
  g.add(neck);

  const head = mesh(
    geo('gplush-head', () => new THREE.SphereGeometry(0.072, 12, 10)),
    fur,
  );
  head.scale.set(1, 1.04, 1.14);
  head.position.set(0, 0.428, -0.082);
  g.add(head);

  const bill = mesh(
    geo('gplush-bill', () => {
      const c = new THREE.ConeGeometry(0.034, 0.088, 8);
      c.rotateX(-Math.PI / 2);
      c.scale(1.2, 0.62, 1);
      return c;
    }),
    billMat,
  );
  bill.position.set(0, 0.418, -0.158);
  g.add(bill);

  for (const s of [-1, 1]) {
    const eye = stitchEye();
    eye.position.set(0.036 * s, 0.452, -0.132);
    eye.scale.setScalar(1.15);
    g.add(eye);

    // Stubby wings, wrapped forward around the heart.
    const wing = mesh(
      geo('gplush-wing', () => new THREE.SphereGeometry(0.086, 9, 7)),
      fur,
    );
    wing.scale.set(0.40, 0.80, 1.05);
    wing.position.set(0.135 * s, 0.180, -0.070);
    wing.rotation.y = -0.5 * s;
    g.add(wing);

    const foot = mesh(
      geo('gplush-foot', () => new THREE.SphereGeometry(0.036, 8, 6)),
      billMat,
      { receive: false },
    );
    foot.scale.set(1, 0.36, 1.35);
    foot.position.set(0.058 * s, 0.020, -0.052);
    g.add(foot);
  }

  const heart = mesh(
    heartGeo(0.115),
    mat(0xffffff, { rough: 0.95, map: plushFelt(0xe8607a) }),
  );
  heart.position.set(0, 0.185, -0.155);
  heart.rotation.x = 0.18;
  heart.name = 'heart';
  g.add(heart);

  return g;
}

/**
 * A plush prize.
 * @param {'small'|'large'|'plush-small'|'plush-large'} tier
 * @returns {THREE.Group} with `userData.tier`.
 */
export function createPlush(tier = 'small') {
  const large = String(tier).includes('large');
  const g = large ? createGoosePlush() : createTeddy();
  g.userData.tier = large ? 'large' : 'small';
  return g;
}

/**
 * A goldfish (GDD 9.3).
 * @param {'orange'|'spotted'|'golden'|string} variant
 * @returns {THREE.Group} with `userData.parts` and a `userData.wiggle(t)` hook.
 */
export function createFish(variant = 'orange') {
  const g = new THREE.Group();
  g.name = `fish-${variant}`;

  const base = { orange: 0xff8c42, spotted: 0xff9a52, golden: 0xffd23f }[variant] ?? 0xff8c42;
  const skin = variant === 'spotted'
    ? mat(0xffffff, { rough: 0.42, map: fishScales(base, 0xfdf3e0) })
    : mat(0xffffff, {
      rough: variant === 'golden' ? 0.24 : 0.42,
      metal: variant === 'golden' ? 0.35 : 0.03,
      map: fishScales(base),
      emissive: variant === 'golden' ? 0x4a3400 : 0x000000,
      emissiveIntensity: variant === 'golden' ? 0.55 : 0,
    });

  // A fat little body: round at the shoulder, pinched to a wrist at the tail.
  const body = mesh(
    geo('fish-body', () => {
      const b = new THREE.SphereGeometry(0.036, 14, 11);
      const pos = b.attributes.position;
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        const t = (v.x / 0.036 + 1) / 2;        // 0 nose, 1 tail
        const pinch = 1 - 0.78 * Math.pow(Math.max(0, t - 0.45) / 0.55, 1.7);
        v.y *= pinch;
        v.z *= pinch * 0.62;
        v.x *= 1.55;
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      b.computeVertexNormals();
      return b;
    }),
    skin,
  );
  g.add(body);

  const finMat = mat(0xffffff, {
    rough: 0.5, transparent: true, opacity: 0.82,
    side: THREE.DoubleSide, map: fishScales(tint(base, 0.30)),
  });

  // Tail: two layered lobes rather than one cone, which is what gives it a
  // silhouette when it flicks.
  const tail = new THREE.Group();
  tail.name = 'tail';
  tail.position.x = 0.052;
  g.add(tail);
  for (const s of [-1, 1]) {
    const lobe = mesh(
      geo('fish-tail-lobe', () => {
        const sh = new THREE.Shape();
        sh.moveTo(0, 0);
        sh.quadraticCurveTo(0.020, 0.018, 0.040, 0.036);
        sh.quadraticCurveTo(0.030, 0.008, 0.034, -0.004);
        sh.quadraticCurveTo(0.018, 0.000, 0, 0);
        return new THREE.ShapeGeometry(sh, 4);
      }),
      finMat,
      { receive: false },
    );
    lobe.scale.y = s;
    lobe.rotation.x = 0.25 * s;
    tail.add(lobe);
  }

  // Dorsal and pectoral fins.
  const dorsal = mesh(
    geo('fish-dorsal', () => {
      const sh = new THREE.Shape();
      sh.moveTo(-0.020, 0);
      sh.quadraticCurveTo(-0.004, 0.030, 0.018, 0.024);
      sh.quadraticCurveTo(0.014, 0.006, 0.022, 0);
      sh.closePath();
      const gg = new THREE.ShapeGeometry(sh, 4);
      gg.rotateY(Math.PI / 2);
      return gg;
    }),
    finMat,
    { receive: false },
  );
  dorsal.position.set(0.002, 0.020, 0);
  g.add(dorsal);

  for (const s of [-1, 1]) {
    const pec = mesh(
      geo('fish-pec', () => {
        const sh = new THREE.Shape();
        sh.moveTo(0, 0);
        sh.quadraticCurveTo(0.014, -0.012, 0.028, -0.006);
        sh.quadraticCurveTo(0.016, 0.004, 0, 0);
        return new THREE.ShapeGeometry(sh, 4);
      }),
      finMat,
      { receive: false },
    );
    pec.position.set(-0.004, -0.004, 0.014 * s);
    pec.rotation.set(0, 0, 0);
    pec.rotation.y = -0.5 * s;
    pec.rotation.x = Math.PI / 2 * s;
    g.add(pec);
  }

  // The big eye is most of the charm.
  for (const s of [-1, 1]) {
    const white = mesh(
      geo('fish-eye-white', () => new THREE.SphereGeometry(0.011, 9, 7)),
      mat(0xfdfaf2, { rough: 0.35 }),
      { cast: false },
    );
    white.position.set(-0.036, 0.010, 0.014 * s);
    g.add(white);

    const pupil = mesh(
      geo('fish-pupil', () => new THREE.SphereGeometry(0.0062, 7, 6)),
      mat(ART.ink, { rough: 0.2 }),
      { cast: false, receive: false },
    );
    pupil.position.set(-0.043, 0.010, 0.017 * s);
    g.add(pupil);

    // Emissive-lit rather than unlit. The fish is the most tinted object in
    // the game — the tank recolours rare variants by walking its materials —
    // and an unlit MeshBasicMaterial has no emissive channel to write to.
    // This reads just as bright and survives setEmissive().
    const glint = mesh(
      geo('fish-glint', () => new THREE.SphereGeometry(0.0022, 5, 4)),
      mat(0xffffff, { rough: 0.4, emissive: 0xffffff, emissiveIntensity: 0.9 }),
      { cast: false, receive: false },
    );
    glint.position.set(-0.046, 0.014, 0.019 * s);
    g.add(glint);
  }

  g.userData.variant = variant;
  g.userData.parts = { body, tail, dorsal };
  /**
   * Swim animation hook — the tank and the payout panel both drive this.
   * @param {number} t Elapsed seconds.
   */
  g.userData.wiggle = (t) => {
    tail.rotation.y = Math.sin(t * 6.5) * 0.55;
    g.rotation.z = Math.sin(t * 3.2) * 0.06;
  };
  return g;
}

/** A single ADMIT ONE ticket. @returns {THREE.Mesh} */
export function createTicket() {
  const m = mesh(
    geo('ticket', () => new THREE.BoxGeometry(0.09, 0.045, 0.002)),
    mat(0xffffff, { rough: 0.72, map: ticketFace() }),
  );
  m.name = 'ticket';
  return m;
}

/** A redemption token: plum, struck with a star. @returns {THREE.Mesh} */
export function createToken() {
  const face = paint('token-face', 256, 256, (ctx, w, h) => {
    const cx = w / 2, cy = h / 2;
    const grad = ctx.createRadialGradient(cx * 0.75, cy * 0.75, 4, cx, cy, w * 0.55);
    grad.addColorStop(0, `#${tint(ART.plum, 0.28).toString(16)}`);
    grad.addColorStop(1, `#${shade(ART.plum, 0.30).toString(16)}`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = `#${ART.gold.toString(16)}`;
    ctx.lineWidth = 10;
    ctx.beginPath(); ctx.arc(cx, cy, w * 0.40, 0, Math.PI * 2); ctx.stroke();

    ctx.fillStyle = `#${ART.gold.toString(16)}`;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
      const rr = i % 2 ? w * 0.14 : w * 0.30;
      const fn = i ? 'lineTo' : 'moveTo';
      ctx[fn](cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
    }
    ctx.closePath();
    ctx.fill();

    showcardText(ctx, 'TOKEN', cx, cy + w * 0.36, {
      size: 30, fill: `#${ART.gold.toString(16)}`, outline: 'rgba(0,0,0,0.5)',
    });
  });

  const m = mesh(
    geo('token', () => new THREE.CylinderGeometry(0.022, 0.022, 0.004, 20)),
    mat(0xffffff, { rough: 0.42, metal: 0.35, map: face }),
  );
  m.name = 'token';
  return m;
}
